/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

const /** @type {?} */ parse5 = require('parse5');

import {ListWrapper} from '../src/facade/collection';
import {DomAdapter, setRootDomAdapter} from './private_import_platform-browser';
import {isPresent, isBlank, global, setValueOnPath} from '../src/facade/lang';
import {SelectorMatcher, CssSelector} from '@angular/compiler';

let /** @type {?} */ treeAdapter: any;

const /** @type {?} */ _attrToPropMap: {[key: string]: string} = {
  'class': 'className',
  'innerHtml': 'innerHTML',
  'readonly': 'readOnly',
  'tabindex': 'tabIndex',
};

let /** @type {?} */ defDoc: any = null;

const /** @type {?} */ mapProps = ['attribs', 'x-attribsNamespace', 'x-attribsPrefix'];
/**
 * @param {?} methodName
 * @return {?}
 */
function _notImplemented(methodName: string) {
  return new Error('This method is not implemented in Parse5DomAdapter: ' + methodName);
}
/**
 *  A `DomAdapter` powered by the `parse5` NodeJS module.
  * *
  * can introduce XSS risks.
 */
export class Parse5DomAdapter extends DomAdapter {
/**
 * @return {?}
 */
static makeCurrent() {
    treeAdapter = parse5.treeAdapters.htmlparser2;
    setRootDomAdapter(new Parse5DomAdapter());
  }
/**
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
hasProperty(element: any, name: string): boolean {
    return _HTMLElementPropertyList.indexOf(name) > -1;
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: any, name: string, value: any) {
    if (name === 'innerHTML') {
      this.setInnerHTML(el, value);
    } else if (name === 'className') {
      el.attribs['class'] = el.className = value;
    } else {
      el[name] = value;
    }
  }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
getProperty(el: any, name: string): any { return el[name]; }
/**
 * @param {?} error
 * @return {?}
 */
logError(error: string) { console.error(error); }
/**
 * @param {?} error
 * @return {?}
 */
log(error: string) { console.log(error); }
/**
 * @param {?} error
 * @return {?}
 */
logGroup(error: string) { console.error(error); }
/**
 * @return {?}
 */
logGroupEnd() {}
/**
 * @return {?}
 */
get attrToPropMap() { return _attrToPropMap; }
/**
 * @param {?} selector
 * @return {?}
 */
query(selector: any) { throw _notImplemented('query'); }
/**
 * @param {?} el
 * @param {?} selector
 * @return {?}
 */
querySelector(el: any, selector: string): any { return this.querySelectorAll(el, selector)[0]; }
/**
 * @param {?} el
 * @param {?} selector
 * @return {?}
 */
querySelectorAll(el: any, selector: string): any[] {
    const /** @type {?} */ res: any[] = [];
    const /** @type {?} */ _recursive = (result: any, node: any, selector: any, matcher: any) => {
      const /** @type {?} */ cNodes = node.childNodes;
      if (cNodes && cNodes.length > 0) {
        for (let /** @type {?} */ i = 0; i < cNodes.length; i++) {
          const /** @type {?} */ childNode = cNodes[i];
          if (this.elementMatches(childNode, selector, matcher)) {
            result.push(childNode);
          }
          _recursive(result, childNode, selector, matcher);
        }
      }
    };
    const /** @type {?} */ matcher = new SelectorMatcher();
    matcher.addSelectables(CssSelector.parse(selector));
    _recursive(res, el, selector, matcher);
    return res;
  }
/**
 * @param {?} node
 * @param {?} selector
 * @param {?=} matcher
 * @return {?}
 */
elementMatches(node: any, selector: string, matcher: any = null): boolean {
    if (this.isElementNode(node) && selector === '*') {
      return true;
    }
    let /** @type {?} */ result = false;
    if (selector && selector.charAt(0) == '#') {
      result = this.getAttribute(node, 'id') == selector.substring(1);
    } else if (selector) {
      if (!matcher) {
        matcher = new SelectorMatcher();
        matcher.addSelectables(CssSelector.parse(selector));
      }

      const /** @type {?} */ cssSelector = new CssSelector();
      cssSelector.setElement(this.tagName(node));
      if (node.attribs) {
        for (const /** @type {?} */ attrName in node.attribs) {
          cssSelector.addAttribute(attrName, node.attribs[attrName]);
        }
      }
      const /** @type {?} */ classList = this.classList(node);
      for (let /** @type {?} */ i = 0; i < classList.length; i++) {
        cssSelector.addClassName(classList[i]);
      }

      matcher.match(cssSelector, function(selector: any, cb: any) { result = true; });
    }
    return result;
  }
/**
 * @param {?} el
 * @param {?} evt
 * @param {?} listener
 * @return {?}
 */
on(el: any, evt: any, listener: any) {
    let /** @type {?} */ listenersMap: {[k: string]: any} = el._eventListenersMap;
    if (!listenersMap) {
      listenersMap = {};
      el._eventListenersMap = listenersMap;
    }
    const /** @type {?} */ listeners = listenersMap[evt] || [];
    listenersMap[evt] = [...listeners, listener];
  }
/**
 * @param {?} el
 * @param {?} evt
 * @param {?} listener
 * @return {?}
 */
onAndCancel(el: any, evt: any, listener: any): Function {
    this.on(el, evt, listener);
    return () => { ListWrapper.remove( /** @type {?} */((<any[]>(el._eventListenersMap[evt]))), listener); };
  }
/**
 * @param {?} el
 * @param {?} evt
 * @return {?}
 */
dispatchEvent(el: any, evt: any) {
    if (!evt.target) {
      evt.target = el;
    }
    if (el._eventListenersMap) {
      const /** @type {?} */ listeners: any = el._eventListenersMap[evt.type];
      if (listeners) {
        for (let /** @type {?} */ i = 0; i < listeners.length; i++) {
          listeners[i](evt);
        }
      }
    }
    if (el.parent) {
      this.dispatchEvent(el.parent, evt);
    }
    if (el._window) {
      this.dispatchEvent(el._window, evt);
    }
  }
/**
 * @param {?} eventType
 * @return {?}
 */
createMouseEvent(eventType: any): Event { return this.createEvent(eventType); }
/**
 * @param {?} eventType
 * @return {?}
 */
createEvent(eventType: string): Event {
    const /** @type {?} */ event = /** @type {?} */(( <Event>{
      type: eventType,
      defaultPrevented: false,
      preventDefault: () => { ( /** @type {?} */((<any>event))).defaultPrevented = true; }
    }));
    return event;
  }
/**
 * @param {?} event
 * @return {?}
 */
preventDefault(event: any) { event.returnValue = false; }
/**
 * @param {?} event
 * @return {?}
 */
isPrevented(event: any): boolean { return isPresent(event.returnValue) && !event.returnValue; }
/**
 * @param {?} el
 * @return {?}
 */
getInnerHTML(el: any): string {
    return parse5.serialize(this.templateAwareRoot(el), {treeAdapter});
  }
/**
 * @param {?} el
 * @return {?}
 */
getTemplateContent(el: any): Node { return null; }
/**
 * @param {?} el
 * @return {?}
 */
getOuterHTML(el: any): string {
    const /** @type {?} */ fragment = treeAdapter.createDocumentFragment();
    this.appendChild(fragment, el);
    return parse5.serialize(fragment, {treeAdapter});
  }
/**
 * @param {?} node
 * @return {?}
 */
nodeName(node: any): string { return node.tagName; }
/**
 * @param {?} node
 * @return {?}
 */
nodeValue(node: any): string { return node.nodeValue; }
/**
 * @param {?} node
 * @return {?}
 */
type(node: any): string { throw _notImplemented('type'); }
/**
 * @param {?} node
 * @return {?}
 */
content(node: any): string { return node.childNodes[0]; }
/**
 * @param {?} el
 * @return {?}
 */
firstChild(el: any): Node { return el.firstChild; }
/**
 * @param {?} el
 * @return {?}
 */
nextSibling(el: any): Node { return el.nextSibling; }
/**
 * @param {?} el
 * @return {?}
 */
parentElement(el: any): Node { return el.parent; }
/**
 * @param {?} el
 * @return {?}
 */
childNodes(el: any): Node[] { return el.childNodes; }
/**
 * @param {?} el
 * @return {?}
 */
childNodesAsList(el: any): any[] {
    const /** @type {?} */ childNodes = el.childNodes;
    const /** @type {?} */ res = new Array(childNodes.length);
    for (let /** @type {?} */ i = 0; i < childNodes.length; i++) {
      res[i] = childNodes[i];
    }
    return res;
  }
/**
 * @param {?} el
 * @return {?}
 */
clearNodes(el: any) {
    while (el.childNodes.length > 0) {
      this.remove(el.childNodes[0]);
    }
  }
/**
 * @param {?} el
 * @param {?} node
 * @return {?}
 */
appendChild(el: any, node: any) {
    this.remove(node);
    treeAdapter.appendChild(this.templateAwareRoot(el), node);
  }
/**
 * @param {?} el
 * @param {?} node
 * @return {?}
 */
removeChild(el: any, node: any) {
    if (el.childNodes.indexOf(node) > -1) {
      this.remove(node);
    }
  }
/**
 * @param {?} el
 * @return {?}
 */
remove(el: any): HTMLElement {
    const /** @type {?} */ parent = el.parent;
    if (parent) {
      const /** @type {?} */ index = parent.childNodes.indexOf(el);
      parent.childNodes.splice(index, 1);
    }
    const /** @type {?} */ prev = el.previousSibling;
    const /** @type {?} */ next = el.nextSibling;
    if (prev) {
      prev.next = next;
    }
    if (next) {
      next.prev = prev;
    }
    el.prev = null;
    el.next = null;
    el.parent = null;
    return el;
  }
/**
 * @param {?} el
 * @param {?} node
 * @return {?}
 */
insertBefore(el: any, node: any) {
    this.remove(node);
    treeAdapter.insertBefore(el.parent, node, el);
  }
/**
 * @param {?} el
 * @param {?} nodes
 * @return {?}
 */
insertAllBefore(el: any, nodes: any) { nodes.forEach((n: any) => this.insertBefore(el, n)); }
/**
 * @param {?} el
 * @param {?} node
 * @return {?}
 */
insertAfter(el: any, node: any) {
    if (el.nextSibling) {
      this.insertBefore(el.nextSibling, node);
    } else {
      this.appendChild(el.parent, node);
    }
  }
/**
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setInnerHTML(el: any, value: any) {
    this.clearNodes(el);
    const /** @type {?} */ content = parse5.parseFragment(value, {treeAdapter});
    for (let /** @type {?} */ i = 0; i < content.childNodes.length; i++) {
      treeAdapter.appendChild(el, content.childNodes[i]);
    }
  }
/**
 * @param {?} el
 * @param {?=} isRecursive
 * @return {?}
 */
getText(el: any, isRecursive?: boolean): string {
    if (this.isTextNode(el)) {
      return el.data;
    }

    if (this.isCommentNode(el)) {
      // In the DOM, comments within an element return an empty string for textContent
      // However, comment node instances return the comment content for textContent getter
      return isRecursive ? '' : el.data;
    }

    if (!el.childNodes || el.childNodes.length == 0) {
      return '';
    }

    let /** @type {?} */ textContent = '';
    for (let /** @type {?} */ i = 0; i < el.childNodes.length; i++) {
      textContent += this.getText(el.childNodes[i], true);
    }
    return textContent;
  }
/**
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setText(el: any, value: string) {
    if (this.isTextNode(el) || this.isCommentNode(el)) {
      el.data = value;
    } else {
      this.clearNodes(el);
      if (value !== '') treeAdapter.insertText(el, value);
    }
  }
/**
 * @param {?} el
 * @return {?}
 */
getValue(el: any): string { return el.value; }
/**
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setValue(el: any, value: string) { el.value = value; }
/**
 * @param {?} el
 * @return {?}
 */
getChecked(el: any): boolean { return el.checked; }
/**
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setChecked(el: any, value: boolean) { el.checked = value; }
/**
 * @param {?} text
 * @return {?}
 */
createComment(text: string): Comment { return treeAdapter.createCommentNode(text); }
/**
 * @param {?} html
 * @return {?}
 */
createTemplate(html: any): HTMLElement {
    const /** @type {?} */ template = treeAdapter.createElement('template', 'http://www.w3.org/1999/xhtml', []);
    const /** @type {?} */ content = parse5.parseFragment(html, {treeAdapter});
    treeAdapter.setTemplateContent(template, content);
    return template;
  }
/**
 * @param {?} tagName
 * @return {?}
 */
createElement(tagName: any): HTMLElement {
    return treeAdapter.createElement(tagName, 'http://www.w3.org/1999/xhtml', []);
  }
/**
 * @param {?} ns
 * @param {?} tagName
 * @return {?}
 */
createElementNS(ns: any, tagName: any): HTMLElement {
    return treeAdapter.createElement(tagName, ns, []);
  }
/**
 * @param {?} text
 * @return {?}
 */
createTextNode(text: string): Text {
    const /** @type {?} */ t = /** @type {?} */(( <any>this.createComment(text)));
    t.type = 'text';
    return t;
  }
/**
 * @param {?} attrName
 * @param {?} attrValue
 * @return {?}
 */
createScriptTag(attrName: string, attrValue: string): HTMLElement {
    return treeAdapter.createElement(
        'script', 'http://www.w3.org/1999/xhtml', [{name: attrName, value: attrValue}]);
  }
/**
 * @param {?} css
 * @return {?}
 */
createStyleElement(css: string): HTMLStyleElement {
    const /** @type {?} */ style = this.createElement('style');
    this.setText(style, css);
    return /** @type {?} */(( <HTMLStyleElement>style));
  }
/**
 * @param {?} el
 * @return {?}
 */
createShadowRoot(el: any): HTMLElement {
    el.shadowRoot = treeAdapter.createDocumentFragment();
    el.shadowRoot.parent = el;
    return el.shadowRoot;
  }
/**
 * @param {?} el
 * @return {?}
 */
getShadowRoot(el: any): Element { return el.shadowRoot; }
/**
 * @param {?} el
 * @return {?}
 */
getHost(el: any): string { return el.host; }
/**
 * @param {?} el
 * @return {?}
 */
getDistributedNodes(el: any): Node[] { throw _notImplemented('getDistributedNodes'); }
/**
 * @param {?} node
 * @return {?}
 */
clone(node: Node): Node {
    const /** @type {?} */ _recursive = (node: any) => {
      const /** @type {?} */ nodeClone = Object.create(Object.getPrototypeOf(node));
      for (const /** @type {?} */ prop in node) {
        const /** @type {?} */ desc = Object.getOwnPropertyDescriptor(node, prop);
        if (desc && 'value' in desc && typeof desc.value !== 'object') {
          nodeClone[prop] = node[prop];
        }
      }
      nodeClone.parent = null;
      nodeClone.prev = null;
      nodeClone.next = null;
      nodeClone.children = null;

      mapProps.forEach(mapName => {
        if (isPresent(node[mapName])) {
          nodeClone[mapName] = {};
          for (const /** @type {?} */ prop in node[mapName]) {
            nodeClone[mapName][prop] = node[mapName][prop];
          }
        }
      });
      const /** @type {?} */ cNodes = node.children;
      if (cNodes) {
        const /** @type {?} */ cNodesClone = new Array(cNodes.length);
        for (let /** @type {?} */ i = 0; i < cNodes.length; i++) {
          const /** @type {?} */ childNode = cNodes[i];
          const /** @type {?} */ childNodeClone = _recursive(childNode);
          cNodesClone[i] = childNodeClone;
          if (i > 0) {
            childNodeClone.prev = cNodesClone[i - 1];
            cNodesClone[i - 1].next = childNodeClone;
          }
          childNodeClone.parent = nodeClone;
        }
        nodeClone.children = cNodesClone;
      }
      return nodeClone;
    };
    return _recursive(node);
  }
/**
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
getElementsByClassName(element: any, name: string): HTMLElement[] {
    return this.querySelectorAll(element, '.' + name);
  }
/**
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
getElementsByTagName(element: any, name: string): HTMLElement[] {
    throw _notImplemented('getElementsByTagName');
  }
/**
 * @param {?} element
 * @return {?}
 */
classList(element: any): string[] {
    let /** @type {?} */ classAttrValue: any = null;
    const /** @type {?} */ attributes = element.attribs;
    if (attributes && attributes.hasOwnProperty('class')) {
      classAttrValue = attributes['class'];
    }
    return classAttrValue ? classAttrValue.trim().split(/\s+/g) : [];
  }
/**
 * @param {?} element
 * @param {?} className
 * @return {?}
 */
addClass(element: any, className: string) {
    const /** @type {?} */ classList = this.classList(element);
    const /** @type {?} */ index = classList.indexOf(className);
    if (index == -1) {
      classList.push(className);
      element.attribs['class'] = element.className = classList.join(' ');
    }
  }
/**
 * @param {?} element
 * @param {?} className
 * @return {?}
 */
removeClass(element: any, className: string) {
    const /** @type {?} */ classList = this.classList(element);
    const /** @type {?} */ index = classList.indexOf(className);
    if (index > -1) {
      classList.splice(index, 1);
      element.attribs['class'] = element.className = classList.join(' ');
    }
  }
/**
 * @param {?} element
 * @param {?} className
 * @return {?}
 */
hasClass(element: any, className: string): boolean {
    return this.classList(element).indexOf(className) > -1;
  }
/**
 * @param {?} element
 * @param {?} styleName
 * @param {?=} styleValue
 * @return {?}
 */
hasStyle(element: any, styleName: string, styleValue: string = null): boolean {
    const /** @type {?} */ value = this.getStyle(element, styleName) || '';
    return styleValue ? value == styleValue : value.length > 0;
  }
/**
 * @param {?} element
 * @return {?}
 */
_readStyleAttribute(element: any) {
    const /** @type {?} */ styleMap = {};
    const /** @type {?} */ attributes = element.attribs;
    if (attributes && attributes.hasOwnProperty('style')) {
      const /** @type {?} */ styleAttrValue = attributes['style'];
      const /** @type {?} */ styleList = styleAttrValue.split(/;+/g);
      for (let /** @type {?} */ i = 0; i < styleList.length; i++) {
        if (styleList[i].length > 0) {
          const /** @type {?} */ elems = styleList[i].split(/:+/g);
          ( /** @type {?} */((styleMap as any)))[elems[0].trim()] = elems[1].trim();
        }
      }
    }
    return styleMap;
  }
/**
 * @param {?} element
 * @param {?} styleMap
 * @return {?}
 */
_writeStyleAttribute(element: any, styleMap: any) {
    let /** @type {?} */ styleAttrValue = '';
    for (const /** @type {?} */ key in styleMap) {
      const /** @type {?} */ newValue = styleMap[key];
      if (newValue) {
        styleAttrValue += key + ':' + styleMap[key] + ';';
      }
    }
    element.attribs['style'] = styleAttrValue;
  }
/**
 * @param {?} element
 * @param {?} styleName
 * @param {?} styleValue
 * @return {?}
 */
setStyle(element: any, styleName: string, styleValue: string) {
    const /** @type {?} */ styleMap = this._readStyleAttribute(element);
    ( /** @type {?} */((styleMap as any)))[styleName] = styleValue;
    this._writeStyleAttribute(element, styleMap);
  }
/**
 * @param {?} element
 * @param {?} styleName
 * @return {?}
 */
removeStyle(element: any, styleName: string) { this.setStyle(element, styleName, null); }
/**
 * @param {?} element
 * @param {?} styleName
 * @return {?}
 */
getStyle(element: any, styleName: string): string {
    const /** @type {?} */ styleMap = this._readStyleAttribute(element);
    return styleMap.hasOwnProperty(styleName) ? ( /** @type {?} */((styleMap as any)))[styleName] : '';
  }
/**
 * @param {?} element
 * @return {?}
 */
tagName(element: any): string { return element.tagName == 'style' ? 'STYLE' : element.tagName; }
/**
 * @param {?} element
 * @return {?}
 */
attributeMap(element: any): Map<string, string> {
    const /** @type {?} */ res = new Map<string, string>();
    const /** @type {?} */ elAttrs = treeAdapter.getAttrList(element);
    for (let /** @type {?} */ i = 0; i < elAttrs.length; i++) {
      const /** @type {?} */ attrib = elAttrs[i];
      res.set(attrib.name, attrib.value);
    }
    return res;
  }
/**
 * @param {?} element
 * @param {?} attribute
 * @return {?}
 */
hasAttribute(element: any, attribute: string): boolean {
    return element.attribs && element.attribs.hasOwnProperty(attribute);
  }
/**
 * @param {?} element
 * @param {?} ns
 * @param {?} attribute
 * @return {?}
 */
hasAttributeNS(element: any, ns: string, attribute: string): boolean { throw 'not implemented'; }
/**
 * @param {?} element
 * @param {?} attribute
 * @return {?}
 */
getAttribute(element: any, attribute: string): string {
    return element.attribs && element.attribs.hasOwnProperty(attribute) ?
        element.attribs[attribute] :
        null;
  }
/**
 * @param {?} element
 * @param {?} ns
 * @param {?} attribute
 * @return {?}
 */
getAttributeNS(element: any, ns: string, attribute: string): string { throw 'not implemented'; }
/**
 * @param {?} element
 * @param {?} attribute
 * @param {?} value
 * @return {?}
 */
setAttribute(element: any, attribute: string, value: string) {
    if (attribute) {
      element.attribs[attribute] = value;
      if (attribute === 'class') {
        element.className = value;
      }
    }
  }
/**
 * @param {?} element
 * @param {?} ns
 * @param {?} attribute
 * @param {?} value
 * @return {?}
 */
setAttributeNS(element: any, ns: string, attribute: string, value: string) {
    throw 'not implemented';
  }
/**
 * @param {?} element
 * @param {?} attribute
 * @return {?}
 */
removeAttribute(element: any, attribute: string) {
    if (attribute) {
      delete element.attribs[attribute];
    }
  }
/**
 * @param {?} element
 * @param {?} ns
 * @param {?} name
 * @return {?}
 */
removeAttributeNS(element: any, ns: string, name: string) { throw 'not implemented'; }
/**
 * @param {?} el
 * @return {?}
 */
templateAwareRoot(el: any): any {
    return this.isTemplateElement(el) ? treeAdapter.getTemplateContent(el) : el;
  }
/**
 * @return {?}
 */
createHtmlDocument(): Document {
    const /** @type {?} */ newDoc = treeAdapter.createDocument();
    newDoc.title = 'fake title';
    const /** @type {?} */ head = treeAdapter.createElement('head', null, []);
    const /** @type {?} */ body = treeAdapter.createElement('body', 'http://www.w3.org/1999/xhtml', []);
    this.appendChild(newDoc, head);
    this.appendChild(newDoc, body);
    newDoc['head'] = head;
    newDoc['body'] = body;
    newDoc['_window'] = {};
    return newDoc;
  }
/**
 * @return {?}
 */
defaultDoc(): Document { return defDoc = defDoc || this.createHtmlDocument(); }
/**
 * @param {?} el
 * @return {?}
 */
getBoundingClientRect(el: any): any { return {left: 0, top: 0, width: 0, height: 0}; }
/**
 * @return {?}
 */
getTitle(): string { return this.defaultDoc().title || ''; }
/**
 * @param {?} newTitle
 * @return {?}
 */
setTitle(newTitle: string) { this.defaultDoc().title = newTitle; }
/**
 * @param {?} el
 * @return {?}
 */
isTemplateElement(el: any): boolean {
    return this.isElementNode(el) && this.tagName(el) === 'template';
  }
/**
 * @param {?} node
 * @return {?}
 */
isTextNode(node: any): boolean { return treeAdapter.isTextNode(node); }
/**
 * @param {?} node
 * @return {?}
 */
isCommentNode(node: any): boolean { return treeAdapter.isCommentNode(node); }
/**
 * @param {?} node
 * @return {?}
 */
isElementNode(node: any): boolean { return node ? treeAdapter.isElementNode(node) : false; }
/**
 * @param {?} node
 * @return {?}
 */
hasShadowRoot(node: any): boolean { return isPresent(node.shadowRoot); }
/**
 * @param {?} node
 * @return {?}
 */
isShadowRoot(node: any): boolean { return this.getShadowRoot(node) == node; }
/**
 * @param {?} node
 * @return {?}
 */
importIntoDoc(node: any): any { return this.clone(node); }
/**
 * @param {?} node
 * @return {?}
 */
adoptNode(node: any): any { return node; }
/**
 * @param {?} el
 * @return {?}
 */
getHref(el: any): string { return el.href; }
/**
 * @param {?} el
 * @param {?} baseUrl
 * @param {?} href
 * @return {?}
 */
resolveAndSetHref(el: any, baseUrl: string, href: string) {
    if (href == null) {
      el.href = baseUrl;
    } else {
      el.href = baseUrl + '/../' + href;
    }
  }
/**
 * @param {?} parsedRules
 * @param {?=} css
 * @return {?}
 */
_buildRules(parsedRules: any, css?: any) {
    const /** @type {?} */ rules: any[] = [];
    for (let /** @type {?} */ i = 0; i < parsedRules.length; i++) {
      const /** @type {?} */ parsedRule = parsedRules[i];
      const /** @type {?} */ rule: {[key: string]: any} = {};
      rule['cssText'] = css;
      rule['style'] = {content: '', cssText: ''};
      if (parsedRule.type == 'rule') {
        rule['type'] = 1;

        rule['selectorText'] =
            parsedRule.selectors.join(', '.replace(/\s{2,}/g, ' ')
                                          .replace(/\s*~\s*/g, ' ~ ')
                                          .replace(/\s*\+\s*/g, ' + ')
                                          .replace(/\s*>\s*/g, ' > ')
                                          .replace(/\[(\w+)=(\w+)\]/g, '[$1="$2"]'));
        if (isBlank(parsedRule.declarations)) {
          continue;
        }
        for (let /** @type {?} */ j = 0; j < parsedRule.declarations.length; j++) {
          const /** @type {?} */ declaration = parsedRule.declarations[j];
          rule['style'] = declaration.property[declaration.value];
          rule['style'].cssText += declaration.property + ': ' + declaration.value + ';';
        }
      } else if (parsedRule.type == 'media') {
        rule['type'] = 4;
        rule['media'] = {mediaText: parsedRule.media};
        if (parsedRule.rules) {
          rule['cssRules'] = this._buildRules(parsedRule.rules);
        }
      }
      rules.push(rule);
    }
    return rules;
  }
/**
 * @return {?}
 */
supportsDOMEvents(): boolean { return false; }
/**
 * @return {?}
 */
supportsNativeShadowDOM(): boolean { return false; }
/**
 * @param {?} target
 * @return {?}
 */
getGlobalEventTarget(target: string): any {
    if (target == 'window') {
      return ( /** @type {?} */((<any>this.defaultDoc())))._window;
    } else if (target == 'document') {
      return this.defaultDoc();
    } else if (target == 'body') {
      return this.defaultDoc().body;
    }
  }
/**
 * @return {?}
 */
getBaseHref(): string { throw 'not implemented'; }
/**
 * @return {?}
 */
resetBaseElement(): void { throw 'not implemented'; }
/**
 * @return {?}
 */
getHistory(): History { throw 'not implemented'; }
/**
 * @return {?}
 */
getLocation(): Location { throw 'not implemented'; }
/**
 * @return {?}
 */
getUserAgent(): string { return 'Fake user agent'; }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
getData(el: any, name: string): string { return this.getAttribute(el, 'data-' + name); }
/**
 * @param {?} el
 * @return {?}
 */
getComputedStyle(el: any): any { throw 'not implemented'; }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setData(el: any, name: string, value: string) { this.setAttribute(el, 'data-' + name, value); }
/**
 * @param {?} path
 * @param {?} value
 * @return {?}
 */
setGlobalVar(path: string, value: any) { setValueOnPath(global, path, value); }
/**
 * @return {?}
 */
supportsWebAnimation(): boolean { return false; }
/**
 * @return {?}
 */
performanceNow(): number { return Date.now(); }
/**
 * @return {?}
 */
getAnimationPrefix(): string { return ''; }
/**
 * @return {?}
 */
getTransitionEnd(): string { return 'transitionend'; }
/**
 * @return {?}
 */
supportsAnimation(): boolean { return true; }
/**
 * @param {?} el
 * @param {?} newNode
 * @param {?} oldNode
 * @return {?}
 */
replaceChild(el: any, newNode: any, oldNode: any) { throw new Error('not implemented'); }
/**
 * @param {?} templateHtml
 * @return {?}
 */
parse(templateHtml: string) { throw new Error('not implemented'); }
/**
 * @param {?} el
 * @param {?} methodName
 * @param {?} args
 * @return {?}
 */
invoke(el: Element, methodName: string, args: any[]): any { throw new Error('not implemented'); }
/**
 * @param {?} event
 * @return {?}
 */
getEventKey(event: any): string { throw new Error('not implemented'); }
/**
 * @return {?}
 */
supportsCookies(): boolean { return false; }
/**
 * @param {?} name
 * @return {?}
 */
getCookie(name: string): string { throw new Error('not implemented'); }
/**
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setCookie(name: string, value: string) { throw new Error('not implemented'); }
/**
 * @param {?} element
 * @param {?} keyframes
 * @param {?} options
 * @return {?}
 */
animate(element: any, keyframes: any[], options: any): any { throw new Error('not implemented'); }
}

// TODO: build a proper list, this one is all the keys of a HTMLInputElement
const /** @type {?} */ _HTMLElementPropertyList = [
  'webkitEntries',
  'incremental',
  'webkitdirectory',
  'selectionDirection',
  'selectionEnd',
  'selectionStart',
  'labels',
  'validationMessage',
  'validity',
  'willValidate',
  'width',
  'valueAsNumber',
  'valueAsDate',
  'value',
  'useMap',
  'defaultValue',
  'type',
  'step',
  'src',
  'size',
  'required',
  'readOnly',
  'placeholder',
  'pattern',
  'name',
  'multiple',
  'min',
  'minLength',
  'maxLength',
  'max',
  'list',
  'indeterminate',
  'height',
  'formTarget',
  'formNoValidate',
  'formMethod',
  'formEnctype',
  'formAction',
  'files',
  'form',
  'disabled',
  'dirName',
  'checked',
  'defaultChecked',
  'autofocus',
  'autocomplete',
  'alt',
  'align',
  'accept',
  'onautocompleteerror',
  'onautocomplete',
  'onwaiting',
  'onvolumechange',
  'ontoggle',
  'ontimeupdate',
  'onsuspend',
  'onsubmit',
  'onstalled',
  'onshow',
  'onselect',
  'onseeking',
  'onseeked',
  'onscroll',
  'onresize',
  'onreset',
  'onratechange',
  'onprogress',
  'onplaying',
  'onplay',
  'onpause',
  'onmousewheel',
  'onmouseup',
  'onmouseover',
  'onmouseout',
  'onmousemove',
  'onmouseleave',
  'onmouseenter',
  'onmousedown',
  'onloadstart',
  'onloadedmetadata',
  'onloadeddata',
  'onload',
  'onkeyup',
  'onkeypress',
  'onkeydown',
  'oninvalid',
  'oninput',
  'onfocus',
  'onerror',
  'onended',
  'onemptied',
  'ondurationchange',
  'ondrop',
  'ondragstart',
  'ondragover',
  'ondragleave',
  'ondragenter',
  'ondragend',
  'ondrag',
  'ondblclick',
  'oncuechange',
  'oncontextmenu',
  'onclose',
  'onclick',
  'onchange',
  'oncanplaythrough',
  'oncanplay',
  'oncancel',
  'onblur',
  'onabort',
  'spellcheck',
  'isContentEditable',
  'contentEditable',
  'outerText',
  'innerText',
  'accessKey',
  'hidden',
  'webkitdropzone',
  'draggable',
  'tabIndex',
  'dir',
  'translate',
  'lang',
  'title',
  'childElementCount',
  'lastElementChild',
  'firstElementChild',
  'children',
  'onwebkitfullscreenerror',
  'onwebkitfullscreenchange',
  'nextElementSibling',
  'previousElementSibling',
  'onwheel',
  'onselectstart',
  'onsearch',
  'onpaste',
  'oncut',
  'oncopy',
  'onbeforepaste',
  'onbeforecut',
  'onbeforecopy',
  'shadowRoot',
  'dataset',
  'classList',
  'className',
  'outerHTML',
  'innerHTML',
  'scrollHeight',
  'scrollWidth',
  'scrollTop',
  'scrollLeft',
  'clientHeight',
  'clientWidth',
  'clientTop',
  'clientLeft',
  'offsetParent',
  'offsetHeight',
  'offsetWidth',
  'offsetTop',
  'offsetLeft',
  'localName',
  'prefix',
  'namespaceURI',
  'id',
  'style',
  'attributes',
  'tagName',
  'parentElement',
  'textContent',
  'baseURI',
  'ownerDocument',
  'nextSibling',
  'previousSibling',
  'lastChild',
  'firstChild',
  'childNodes',
  'parentNode',
  'nodeType',
  'nodeValue',
  'nodeName',
  'closure_lm_714617',
  '__jsaction',
];

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
